import { ButtonIcon, FieldGroup, FieldSection, Header, InputExtraMark, SelectDateTime } from '@/ui'
import { Controller } from 'react-hook-form'
import { CheckoutLinkRead, ItemType, StorePromotionUpdate } from '@/api/dashboard'
import { useParams } from 'react-router-dom'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { Button, FormErrorMessage, Input, Select, formatHookFormErrors } from '@dashboard/ui'
import { usePydenticForm } from '@/libs/hooks/usePydenticForm'
import { CloseOutline } from '@/icons'
import { Divider } from '@/components'
import { validateInterval } from '@/layouts/components/ItemTimelimit'
import { URL_NEW } from '@/types'
import {
  useCheckoutLinkCreateMutate,
  useCheckoutLinkQuery,
  useCheckoutLinkUpdateMutate,
} from '@/layouts/checkout-links/api'
import { SelectSkuItemV2 } from '@/layouts/components/SelectSkuItemV2'
import { useEffect, useState } from 'react'
import { useGameItem } from '@/api/useGameItems'

interface CheckoutLinkSettingsProps {
  onClose: () => void
}

enum BenefitType {
  fixed,
  percent,
}

export const CheckoutLinkSettings = (props: CheckoutLinkSettingsProps) => {
  const { t: tMain } = useTranslation()
  const { t } = useTranslation('checkout-links')
  const { companyId, gameId, checkoutLinkId } = useParams() as {
    companyId: string
    gameId: string
    checkoutLinkId: string
  }

  const { mutateAsync: updateMutateAsync } = useCheckoutLinkUpdateMutate(companyId, gameId)
  const { mutateAsync: createMutateAsync } = useCheckoutLinkCreateMutate(companyId, gameId)
  const { data: checkoutLink } = useCheckoutLinkQuery(companyId, gameId, checkoutLinkId)

  const {
    formState: { errors, isSubmitting, isDirty },
    watch,
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    setValue,
  } = usePydenticForm<CheckoutLinkRead>()

  useEffect(() => {
    reset(checkoutLink)
    if (checkoutLink?.bonus_fixed) {
      setBonusType(BenefitType.fixed)
    }
  }, [checkoutLink])

  const [bonusType, setBonusType] = useState<BenefitType>(BenefitType.percent)

  // @ts-ignore
  const [item_ids] = watch(['item_ids'])
  const { data: item = null } = useGameItem(companyId, gameId, item_ids ? item_ids[0] : '')

  const onSubmit = async (data: CheckoutLinkRead) => {
    const err = validateInterval(data.start_at, data.end_at, tMain)
    if (err) {
      setError('start_at', { message: err })
      return
    } else {
      clearErrors('start_at')
    }

    if (checkoutLinkId != URL_NEW) {
      await updateMutateAsync({
        id: data.id,
        update: data as StorePromotionUpdate,
      })
    } else {
      await createMutateAsync({
        create: data,
      })
    }

    props.onClose()
  }

  const renderGeneral = () => {
    return (
      <FieldSection label={t('general')}>
        <Controller
          name="item_ids"
          control={control}
          rules={{ required: t('validation.required') }}
          render={({ field }) => (
            <SelectSkuItemV2
              types={[ItemType.Bundle, ItemType.Item, ItemType.Currency]}
              value={field.value ? field.value[0] : null}
              onChange={v => {
                field.onChange([v])
              }}
            />
          )}
        />
        <ErrorMessage
          name="item_ids"
          errors={errors}
          render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
        />
      </FieldSection>
    )
  }

  const renderTimeLimitations = () => {
    return (
      <FieldSection label={t('table.time-limitations')}>
        <FieldGroup label={tMain('datetime.start_at')}>
          <Controller control={control} name="start_at" render={({ field }) => <SelectDateTime {...field} />} />
          <ErrorMessage
            name="start_at"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
        <FieldGroup label={tMain('datetime.end_at')}>
          <Controller control={control} name="end_at" render={({ field }) => <SelectDateTime {...field} />} />
          <ErrorMessage
            name="end_at"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
      </FieldSection>
    )
  }

  const renderBonusPercentOrFixed = () => {
    if (!item) {
      return null
    }

    if (item.type === ItemType.Bundle) {
      return (
        <FieldSection label={t('benefits')}>
          <FieldGroup label={t('bonus')}>
            <Input
              extraLeft={{
                icon: <InputExtraMark>%</InputExtraMark>,
              }}
              type={'number'}
              {...register('bonus_percent', {
                required: false,
                min: { value: 1, message: t('validation.min', { field: t('bonus'), value: 1 }) },
                max: { value: 1000, message: t('validation.max', { field: t('bonus'), value: 1000 }) },
                setValueAs: (v: string) => {
                  const value = parseInt(v)
                  return isNaN(value) ? undefined : value
                },
              })}
              {...formatHookFormErrors(errors, 'bonus_percent')}
            />
          </FieldGroup>
        </FieldSection>
      )
    }

    if (!item.is_stackable) {
      return null
    }

    return (
      <FieldSection label={t('benefits')}>
        <FieldGroup label={t('bonus-type')} className="w-full">
          <div className="flex gap-2">
            <div className="w-1/2">
              <Select
                value={bonusType}
                items={[
                  {
                    children: t('bonus-type.percent'),
                    value: BenefitType.percent,
                  },
                  { children: t('bonus-type.fixed'), value: BenefitType.fixed },
                ]}
                onChange={v => {
                  setBonusType(v as BenefitType)
                  if (v == BenefitType.percent) {
                    // @ts-ignore
                    setValue('bonus_fixed', null, { shouldDirty: true })
                  } else {
                    setValue('bonus_percent', null, { shouldDirty: true })
                  }
                }}
              />
            </div>
            <div className="w-1/2">
              {bonusType == BenefitType.percent ? (
                <Input
                  key="percent"
                  extraLeft={{
                    icon: <InputExtraMark>%</InputExtraMark>,
                  }}
                  type={'number'}
                  {...register('bonus_percent', {
                    required: false,
                    min: { value: 1, message: t('validation.min', { field: t('bonus'), value: 1 }) },
                    max: { value: 1000, message: t('validation.max', { field: t('bonus'), value: 1000 }) },
                    setValueAs: (v: string) => {
                      const value = parseInt(v)
                      return isNaN(value) ? null : value
                    },
                  })}
                  {...formatHookFormErrors(errors, 'bonus_percent')}
                />
              ) : (
                <Input
                  key="fixed"
                  type={'number'}
                  {...register('bonus_fixed', {
                    required: false,
                    min: { value: 1, message: t('validation.min', { field: t('bonus_fixed'), value: 1 }) },
                    setValueAs: (v: string) => {
                      const value = parseInt(v)
                      return isNaN(value) ? null : value
                    },
                  })}
                  {...formatHookFormErrors(errors, 'bonus_fixed')}
                />
              )}
            </div>
          </div>
        </FieldGroup>
      </FieldSection>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex h-full flex-col bg-fg-primary">
        <Header title={t(checkoutLinkId == URL_NEW ? 'add' : 'edit')} className="sticky top-0 z-10 p-4" minWidth="auto">
          <div className="flex items-center gap-4">
            <Button disabled={!isDirty} loading={isSubmitting} type="submit">
              {tMain('Save')}
            </Button>
            <ButtonIcon onClick={props.onClose} type="button" size="xs" variant="outline" color="secondary">
              <CloseOutline />
            </ButtonIcon>
          </div>
        </Header>

        <div className="flex size-full flex-col p-4">
          {renderGeneral()}
          <Divider />

          {renderTimeLimitations()}
          <Divider />

          {renderBonusPercentOrFixed()}
        </div>
      </div>
    </form>
  )
}
